import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { reportAPI } from '../services/api';
import { FiBarChart2, FiPlus, FiX, FiDownload, FiTrash2, FiFileText, FiCalendar, FiClock } from 'react-icons/fi';
import toast from 'react-hot-toast';

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const { canEdit, isAdmin } = useAuth(); // generating reports is analyst+ on the server
  const [showModal, setShowModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({
    title: '', type: 'annual_health', format: 'pdf',
    dateRange: { start: '', end: '' },
    filters: { region: '', disease: '' },
    description: ''
  });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await reportAPI.getAll();
      setReports(res.data.reports || res.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      await reportAPI.generate(form);
      toast.success('Report generated successfully');
      setShowModal(false);
      setForm({ title: '', type: 'annual_health', format: 'pdf', dateRange: { start: '', end: '' }, filters: { region: '', disease: '' }, description: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to generate report'); }
    setGenerating(false);
  };

  const downloadReport = async (r) => {
    try {
      const { data } = await reportAPI.getById(r._id);
      const report = data.report;
      const name = (report.title || 'report').replace(/[^a-z0-9]+/gi, '_');
      const sections = Object.entries(report.data || {}).filter(([, rows]) => Array.isArray(rows) && rows.length);
      const flat = (row) => Object.fromEntries(Object.entries(row).map(([k, v]) => [k, v && typeof v === 'object' ? (k === '_id' ? Object.values(v).join(' / ') : JSON.stringify(v)) : v]));
      const save = (content, type, ext) => {
        const url = URL.createObjectURL(new Blob([content], { type }));
        const a = document.createElement('a'); a.href = url; a.download = `${name}.${ext}`; a.click();
        URL.revokeObjectURL(url);
      };

      if (report.format === 'csv' || report.format === 'excel') {
        const cell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const lines = [[report.title], [report.content?.summary || ''], []].map(r2 => r2.map(cell).join(','));
        sections.forEach(([section, rows]) => {
          const flatRows = rows.map(flat);
          const cols = [...new Set(flatRows.flatMap(Object.keys))];
          lines.push(cell(section), cols.map(cell).join(','), ...flatRows.map(fr => cols.map(c => cell(fr[c])).join(',')), '');
        });
        save(lines.join('\n'), 'text/csv', 'csv');
        return;
      }

      const esc = (v) => String(v ?? '').replace(/[&<>"]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));
      const tables = sections.map(([section, rows]) => {
        const flatRows = rows.map(flat);
        const cols = [...new Set(flatRows.flatMap(Object.keys))];
        return `<h2>${esc(section)}</h2><table><tr>${cols.map(c => `<th>${esc(c === '_id' ? 'group' : c)}</th>`).join('')}</tr>${flatRows.map(fr => `<tr>${cols.map(c => `<td>${esc(fr[c])}</td>`).join('')}</tr>`).join('')}</table>`;
      }).join('');
      const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(report.title)}</title><style>body{font-family:Arial,sans-serif;max-width:900px;margin:32px auto;color:#0f172a}h1{margin-bottom:4px}table{border-collapse:collapse;width:100%;margin:8px 0 24px}th,td{border:1px solid #e2e8f0;padding:6px 10px;text-align:left;font-size:13px}th{background:#f8fafc}.summary{white-space:pre-wrap;line-height:1.6}</style></head><body><h1>${esc(report.title)}</h1><p style="color:#64748b">${esc(report.type?.replace(/_/g, ' '))} · generated ${new Date(report.createdAt).toLocaleString()}</p><h2>Summary</h2><div class="summary">${esc(report.content?.summary)}</div>${tables}</body></html>`;
      if (report.format === 'pdf') {
        const w = window.open('', '_blank');
        if (!w) { toast.error('Allow pop-ups to download as PDF'); return; }
        w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
      } else {
        save(html, 'text/html', 'html');
      }
    } catch (err) { toast.error('Failed to download report'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await reportAPI.delete(id);
      toast.success('Report deleted');
      load();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const reportTypes = [
    { value: 'annual_health', label: 'Annual Community Health Report', icon: '📊', color: '#3b82f6' },
    { value: 'disease_surveillance', label: 'Disease Surveillance Summary', icon: '🦠', color: '#ef4444' },
    { value: 'vaccination_coverage', label: 'Vaccination Coverage Report', icon: '💉', color: '#10b981' },
    { value: 'health_equity', label: 'Health Equity Report', icon: '⚖️', color: '#8b5cf6' },
    { value: 'vital_statistics', label: 'Vital Statistics Report', icon: '📋', color: '#f59e0b' },
    { value: 'sdoh', label: 'SDOH Analysis Report', icon: '🏘️', color: '#ec4899' },
    { value: 'program_evaluation', label: 'Program Effectiveness Report', icon: '🎯', color: '#06b6d4' },
    { value: 'custom', label: 'Custom Report', icon: '⚙️', color: '#64748b' },
  ];

  const statusBadge = (status) => {
    const map = { completed: { bg: '#dcfce7', color: '#166534', text: 'Completed' }, generating: { bg: '#dbeafe', color: '#1e40af', text: 'Generating...' }, failed: { bg: '#fee2e2', color: '#991b1b', text: 'Failed' }, draft: { bg: '#f1f5f9', color: '#475569', text: 'Draft' } };
    const s = map[status] || map.draft;
    return <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color }}>{s.text}</span>;
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><div style={{ width: 40, height: 40, border: '4px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 className="page-title" style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>Reports</h1>
          <p style={{ color: '#64748b', marginTop: 4 }}>Generate, manage, and export health reports</p>
        </div>
        {canEdit && <button onClick={() => setShowModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
          <FiPlus size={18} /> Generate Report
        </button>}
      </div>

      {/* Quick Generate Templates */}
      <div className="report-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 28 }}>
        {reportTypes.slice(0, 4).map(rt => (
          <div key={rt.value} onClick={() => { setForm({ ...form, type: rt.value, title: rt.label }); setShowModal(true); }}
            style={{ background: '#fff', borderRadius: 14, padding: '20px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 28 }}>{rt.icon}</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{rt.label}</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Quick generate</div>
            </div>
          </div>
        ))}
      </div>

      {/* Reports Table */}
      <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e2e8f0' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700 }}>Generated Reports ({reports.length})</h3>
        </div>
        {reports.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>
            <FiFileText size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
            <p style={{ fontSize: 16, fontWeight: 500 }}>No reports generated yet</p>
            <p style={{ fontSize: 13, marginTop: 4 }}>Click "Generate Report" to create your first report</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Report', 'Type', 'Format', 'Status', 'Created', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#64748b', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reports.map(r => (
                  <tr key={r._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{r.title}</div>
                      {r.description && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{r.description.substring(0, 60)}...</div>}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13 }}>
                      <span style={{ padding: '4px 10px', borderRadius: 6, background: '#f1f5f9', fontSize: 12, fontWeight: 500 }}>{r.type?.replace(/_/g, ' ')}</span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, textTransform: 'uppercase', fontWeight: 600 }}>{r.format}</td>
                    <td style={{ padding: '12px 16px' }}>{statusBadge(r.status)}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748b' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <FiCalendar size={12} /> {new Date(r.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {r.status === 'completed' && (
                          <button onClick={() => downloadReport(r)} title="Download" style={{ background: '#f0fdf4', border: 'none', borderRadius: 6, padding: 6, cursor: 'pointer', color: '#10b981' }}>
                            <FiDownload size={16} />
                          </button>
                        )}
                        {isAdmin && <button onClick={() => handleDelete(r._id)} title="Delete" style={{ background: '#fef2f2', border: 'none', borderRadius: 6, padding: 6, cursor: 'pointer', color: '#ef4444' }}>
                          <FiTrash2 size={16} />
                        </button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Generate Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700 }}>Generate Report</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={20} /></button>
            </div>
            <form onSubmit={handleGenerate}>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Report Title</label>
                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required placeholder="Enter report title" style={inputStyle} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Report Type</label>
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} style={inputStyle}>
                  {reportTypes.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Format</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['pdf', 'excel', 'csv'].map(f => (
                    <button key={f} type="button" onClick={() => setForm({ ...form, format: f })}
                      style={{ flex: 1, padding: '10px', borderRadius: 8, border: form.format === f ? '2px solid #3b82f6' : '1px solid #e2e8f0', background: form.format === f ? '#eff6ff' : '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', textTransform: 'uppercase', color: form.format === f ? '#3b82f6' : '#64748b' }}>
                      {f}
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>Start Date</label>
                  <input type="date" value={form.dateRange.start} onChange={e => setForm({ ...form, dateRange: { ...form.dateRange, start: e.target.value } })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>End Date</label>
                  <input type="date" value={form.dateRange.end} onChange={e => setForm({ ...form, dateRange: { ...form.dateRange, end: e.target.value } })} style={inputStyle} />
                </div>
              </div>
              <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>Region (Optional)</label>
                  <select value={form.filters.region} onChange={e => setForm({ ...form, filters: { ...form.filters, region: e.target.value } })} style={inputStyle}>
                    <option value="">All Regions</option>
                    {['Northeast', 'Southeast', 'Midwest', 'Southwest', 'West Coast', 'Pacific Northwest'].map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Disease (Optional)</label>
                  <input value={form.filters.disease} onChange={e => setForm({ ...form, filters: { ...form.filters, disease: e.target.value } })} placeholder="e.g., COVID-19" style={inputStyle} />
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Describe this report..." style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
              <button type="submit" disabled={generating}
                style={{ width: '100%', padding: 12, background: generating ? '#94a3b8' : '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 15, cursor: generating ? 'not-allowed' : 'pointer' }}>
                {generating ? 'Generating...' : 'Generate Report'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, outline: 'none', boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };

export default Reports;
